import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { normaliseRoomCode, isValidRoomCode } from '@playground/shared';
import { usePlayerStore } from '@/store/player.js';
import { apiFetchWithToken } from '@/platform/api.js';
import {
  GLOBAL_GAMES,
  gameRoute,
  cardThumbCandidates,
  heroPosterCandidates,
} from '@/platform/games.js';

interface GameMeta {
  id: string;
  name: string;
  description: string;
  minPlayers: number;
  maxPlayers: number;
  estimatedDurationMinutes: number;
  tags: string[];
  rewardWin?: number;
  rewardParticipation?: number;
}

type LoadState = 'loading' | 'ready' | 'error';

// ── Visual config ──────────────────────────────────────────────────────────────

const GAME_EMOJI: Record<string, string> = {
  'aether-dynasty':       '🎰',
  'sky-crash':            '✈️',
  'kana-mutti':           '🏺',
  'kamba-adeema':         '🪢',
  'coin-duel':            '🪙',
  'collapse-factor':      '🧱',
  'whack-a-mole':         '🔨',
  'pixel-poker':          '🃏',
  'bubble-rush':          '🫧',
  'moka-crash':           '☕',
  'horse-racing':         '🐎',
  'car-racing':           '🏎️',
  'hangman':              '🎯',
  'word-chain':           '📝',
  'trivia-blitz':         '🧠',
  'bluff-quiz':           '🃏',
  'draw-guess':           '🎨',
  'tictactoe':            '✖️',
  'mafia':                '🕵️',
  'memory-card':          '🃏',
  'rock-paper-scissors':  '🪨',
  'reaction-battle':      '⚡',
  'math-blitz':           '🔢',
  'bingo':                '🎱',
  'higher-or-lower':      '📊',
  'two-truths':           '🤥',
  'wordle-race':          '📖',
  'categories':           '📂',
  'speed-typing':         '⌨️',
  'connect-four':         '🔵',
  'battleship':           '🚢',
  'checkers':             '♟️',
  'emoji-story':          '📖',
};

const GAME_GRADIENT: Record<string, string> = {
  'aether-dynasty':       'from-violet-500 to-fuchsia-700',
  'sky-crash':            'from-sky-500 to-indigo-700',
  'kana-mutti':           'from-orange-500 to-amber-700',
  'kamba-adeema':         'from-amber-600 to-yellow-800',
  'coin-duel':            'from-fuchsia-600 to-indigo-800',
  'collapse-factor':      'from-cyan-600 to-blue-900',
  'whack-a-mole':         'from-lime-500 to-green-800',
  'pixel-poker':          'from-emerald-600 to-green-900',
  'bubble-rush':          'from-sky-400 to-blue-600',
  'moka-crash':           'from-amber-500 to-orange-600',
  'horse-racing':         'from-emerald-500 to-teal-700',
  'car-racing':           'from-red-500 to-orange-700',
  'hangman':              'from-red-400 to-rose-600',
  'word-chain':           'from-emerald-400 to-green-600',
  'trivia-blitz':         'from-violet-400 to-purple-600',
  'bluff-quiz':           'from-fuchsia-400 to-pink-600',
  'draw-guess':           'from-pink-400 to-fuchsia-600',
  'tictactoe':            'from-slate-400 to-gray-600',
  'mafia':                'from-gray-600 to-gray-900',
  'memory-card':          'from-indigo-400 to-indigo-600',
  'rock-paper-scissors':  'from-stone-400 to-stone-600',
  'reaction-battle':      'from-yellow-400 to-orange-500',
  'math-blitz':           'from-cyan-400 to-teal-600',
  'bingo':                'from-lime-400 to-green-600',
  'higher-or-lower':      'from-blue-400 to-cyan-600',
  'two-truths':           'from-rose-400 to-red-600',
  'wordle-race':          'from-green-400 to-emerald-600',
  'categories':           'from-orange-400 to-red-500',
  'speed-typing':         'from-teal-400 to-cyan-600',
  'connect-four':         'from-blue-500 to-indigo-600',
  'battleship':           'from-slate-500 to-blue-800',
  'checkers':             'from-amber-600 to-red-700',
  'emoji-story':          'from-pink-400 to-purple-600',
};

const DEFAULT_EMOJI = '🎮';
const DEFAULT_GRADIENT = 'from-indigo-400 to-indigo-600';

// ── Thumbnail helpers ──────────────────────────────────────────────────────────

/**
 * Walks a list of candidate URLs, advancing on each load error. Renders a
 * gradient + emoji placeholder once every candidate has failed.
 *
 * The thumbnail set mixes .png and .jpg and not every game ships an image,
 * so a hardcoded extension silently degrades every card to an emoji.
 */
function FallbackImage({
  candidates,
  alt,
  gameId,
  className,
  emojiClassName,
  width,
  height,
  loading = 'lazy',
}: {
  candidates: string[];
  alt: string;
  gameId: string;
  className: string;
  emojiClassName: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
}) {
  const [index, setIndex] = useState(0);

  // Reset when the game changes, otherwise a previously-exhausted list would
  // keep the placeholder pinned for the next game rendered in this slot.
  useEffect(() => setIndex(0), [gameId]);

  const src = candidates[index];

  if (src === undefined) {
    const gradient = GAME_GRADIENT[gameId] ?? DEFAULT_GRADIENT;
    const emoji = GAME_EMOJI[gameId] ?? DEFAULT_EMOJI;
    return (
      <div
        role="img"
        aria-label={alt}
        className={`h-full w-full bg-gradient-to-br ${gradient} flex items-center justify-center`}
      >
        <span aria-hidden="true" className={emojiClassName}>{emoji}</span>
      </div>
    );
  }

  return (
    <img
      key={src}
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      onError={() => setIndex((i) => i + 1)}
      className={className}
    />
  );
}

// ── Hero section ───────────────────────────────────────────────────────────────

function HeroSection({ game, onPlay, isFavorite, onToggleFavorite }: {
  game: GameMeta;
  onPlay: () => void;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}) {
  return (
    <section
      aria-labelledby="hero-title"
      className="relative mx-auto w-full max-w-[600px] overflow-hidden"
      // A hard 680px pushed the Play button below the fold on a phone, where
      // the install banner then covered it. Cap against the viewport instead.
      style={{ height: 'min(680px, 68dvh)' }}
    >
      <div className="absolute inset-0">
        <FallbackImage
          candidates={heroPosterCandidates(game.id)}
          alt=""
          gameId={game.id}
          width={600}
          height={680}
          loading="eager"
          className="h-full w-full object-cover"
          emojiClassName="text-7xl"
        />
      </div>

      {/* Darkens the lower half so the title and body copy stay legible. */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Stretched hit target: covers the poster, sits beneath the real
          controls, and gives the whole hero a tap affordance without nesting
          one button inside another. */}
      <button
        type="button"
        onClick={onPlay}
        className="absolute inset-0 z-0 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-white/80"
      >
        <span className="sr-only">Play {game.name}</span>
      </button>

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 px-5 pb-6 text-white">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {GLOBAL_GAMES.has(game.id) && (
            <span className="rounded-full border border-white/20 bg-white/20 px-2 py-0.5 text-[11px] font-semibold backdrop-blur-sm">
              🌐 Global
            </span>
          )}
          <span className="rounded-full border border-white/20 bg-white/20 px-2 py-0.5 text-[11px] tabular-nums backdrop-blur-sm">
            👥 {game.minPlayers}–{game.maxPlayers}
          </span>
          <span className="rounded-full border border-white/20 bg-white/20 px-2 py-0.5 text-[11px] tabular-nums backdrop-blur-sm">
            ⏱ {game.estimatedDurationMinutes}m
          </span>
        </div>
        <h2 id="hero-title" className="text-3xl font-black leading-tight tracking-tight text-balance drop-shadow-lg">
          {game.name}
        </h2>
        <p className="mt-1 max-w-xs text-sm leading-snug text-pretty text-white/80">{game.description}</p>
        <div className="pointer-events-auto mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={onPlay}
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-bold text-gray-900 shadow-lg transition-transform duration-150 ease-out hover:bg-gray-100 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
          >
            ▶ Play
          </button>
          <button
            type="button"
            onClick={onToggleFavorite}
            aria-pressed={isFavorite}
            className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/20 px-5 py-2.5 text-sm font-bold text-white shadow-lg backdrop-blur-sm transition-transform duration-150 ease-out hover:bg-white/30 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
          >
            <span aria-hidden="true">{isFavorite ? '❤️' : '🤍'}</span> Favorite
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Game card ─────────────────────────────────────────────────────────────────

function GameCard({ game, onPlay }: { game: GameMeta; onPlay: () => void }) {
  return (
    <button
      type="button"
      onClick={onPlay}
      className="group flex-none overflow-hidden rounded-xl border border-gray-100 bg-white text-left shadow-sm transition-[transform,box-shadow] duration-150 ease-out hover:shadow-md active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-gray-700 dark:bg-gray-800 dark:focus-visible:ring-offset-gray-950"
      style={{ width: 180 }}
    >
      <div className="relative w-full overflow-hidden" style={{ height: 102 }}>
        <FallbackImage
          candidates={cardThumbCandidates(game.id)}
          alt=""
          gameId={game.id}
          width={180}
          height={102}
          className="block h-full w-full object-cover transition-transform duration-150 ease-out group-hover:scale-[1.03]"
          emojiClassName="text-3xl"
        />
        {GLOBAL_GAMES.has(game.id) && (
          <span
            title="Global room"
            className="absolute right-1.5 top-1.5 rounded-full bg-indigo-600/90 px-1.5 py-0.5 text-[10px] font-bold text-white"
          >
            <span aria-hidden="true">🌐</span>
            <span className="sr-only">Global room</span>
          </span>
        )}
      </div>
      <div className="px-2.5 pb-2.5 pt-2">
        <p className="truncate text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100">{game.name}</p>
        <p className="mt-0.5 text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
          👥 {game.minPlayers}–{game.maxPlayers} · ⏱ {game.estimatedDurationMinutes}m
        </p>
        <p className="mt-0.5 line-clamp-2 text-[11px] leading-tight text-pretty text-gray-500 dark:text-gray-400">
          {game.description}
        </p>
      </div>
    </button>
  );
}

function GameCardSkeleton() {
  return (
    <div
      className="flex-none overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
      style={{ width: 180 }}
    >
      <div className="w-full animate-pulse bg-gray-200 dark:bg-gray-700" style={{ height: 102 }} />
      <div className="space-y-2 px-2.5 pb-2.5 pt-2">
        <div className="h-3.5 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-2.5 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-2.5 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    </div>
  );
}

// ── Category row ───────────────────────────────────────────────────────────────

function CategoryRow({ tag, games, onPlay }: { tag: string; games: GameMeta[]; onPlay: (g: GameMeta) => void }) {
  const headingId = `row-${tag.replace(/\s+/g, '-')}`;
  return (
    <section aria-labelledby={headingId} className="py-3">
      <h2
        id={headingId}
        className="mb-3 px-4 text-sm font-bold uppercase capitalize tracking-widest text-gray-500 dark:text-gray-400"
      >
        {tag}
      </h2>
      <ul className="no-scrollbar flex snap-x snap-mandatory list-none gap-3 overflow-x-auto px-4 pb-2">
        {games.map((game) => (
          <li key={game.id} className="snap-start">
            <GameCard game={game} onPlay={() => onPlay(game)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Home ──────────────────────────────────────────────────────────────────────

export function Home() {
  const navigate = useNavigate();
  const [games, setGames]                       = useState<GameMeta[]>([]);
  const [loadState, setLoadState]               = useState<LoadState>('loading');
  const [maintenanceMode, setMaintenanceMode]   = useState(false);
  const [siteName, setSiteName]                 = useState('Playground');
  const [heroGameId, setHeroGameId]             = useState('');
  const [searchOpen, setSearchOpen]             = useState(false);
  const [search, setSearch]                     = useState('');
  const [joinOpen, setJoinOpen]                 = useState(false);
  const [code, setCode]                         = useState('');
  const [codeError, setCodeError]               = useState('');
  const [filter, setFilter]                     = useState<'all' | 'solo' | 'favorites'>('all');
  const [categoryFilter, setCategoryFilter]     = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const { favorites, toggleFavorite } = usePlayerStore();

  const fetchGames = useCallback((signal?: AbortSignal) => {
    const token = usePlayerStore.getState().token;
    setLoadState((s) => (s === 'ready' ? s : 'loading'));

    apiFetchWithToken('/api/games', token, signal ? { signal } : undefined)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{
          games: GameMeta[];
          maintenanceMode?: boolean;
          siteName?: string;
          heroGameId?: string;
        }>;
      })
      .then((d) => {
        if (signal?.aborted) return;
        setGames(d.games ?? []);
        setMaintenanceMode(d.maintenanceMode ?? false);
        if (d.siteName) setSiteName(d.siteName);
        setHeroGameId(d.heroGameId ?? '');
        setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (signal?.aborted || (err as Error)?.name === 'AbortError') return;
        setLoadState('error');
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchGames(controller.signal);

    function onVisible() {
      if (document.visibilityState === 'visible') fetchGames();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      controller.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchGames]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const heroGame = (heroGameId ? games.find((g) => g.id === heroGameId) : null) ?? games[0] ?? null;

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const g of games) for (const t of g.tags) tagSet.add(t);
    return Array.from(tagSet).sort();
  }, [games]);

  const tagGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    let pool = games;

    if (filter === 'solo') pool = pool.filter((g) => g.minPlayers === 1 || g.tags.includes('solo'));
    if (filter === 'favorites') pool = pool.filter((g) => favorites.includes(g.id));
    if (categoryFilter) pool = pool.filter((g) => g.tags.includes(categoryFilter));

    if (q) {
      // Search name, description and tags — matching on name alone made most
      // games unfindable by what they actually are ("word", "quiz", "racing").
      const filtered = pool.filter((g) =>
        g.name.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.tags.some((t) => t.toLowerCase().includes(q)),
      );
      if (filtered.length === 0) return [];
      return [{ tag: 'results', games: filtered }];
    }

    const tagSet = new Set<string>();
    for (const g of pool) for (const t of g.tags) tagSet.add(t);
    const tags = Array.from(tagSet).sort();
    return tags
      .map((tag) => ({ tag, games: pool.filter((g) => g.tags.includes(tag)) }))
      .filter((row) => row.games.length > 0);
  }, [games, search, filter, categoryFilter, favorites]);

  function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    const normalised = normaliseRoomCode(code);
    if (!isValidRoomCode(normalised)) {
      setCodeError('Enter a valid 6-character room code.');
      return;
    }
    setCodeError('');
    void navigate(`/join?code=${normalised}`);
  }

  const filtersActive = filter !== 'all' || categoryFilter !== '' || search.trim() !== '';

  function clearFilters() {
    setFilter('all');
    setCategoryFilter('');
    setSearch('');
  }

  const chipClass = (active: boolean) =>
    [
      'shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-150',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
      'focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-950',
      active
        ? 'bg-indigo-600 text-white'
        : 'border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800',
    ].join(' ');

  return (
    <div className="min-h-dvh bg-white dark:bg-gray-950">
      <h1 className="sr-only">{siteName} — multiplayer games</h1>

      {/* ── Sticky top bar ──────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-gray-100 bg-white/90 pl-[max(1rem,var(--safe-l))] pr-[max(1rem,var(--safe-r))] pt-[calc(0.75rem+var(--safe-t))] pb-3 backdrop-blur dark:border-gray-800 dark:bg-gray-950/90">
        <button
          type="button"
          onClick={() => void navigate('/')}
          className="flex flex-shrink-0 items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <img src="/favicon.png" alt="" width={28} height={28} className="h-7 w-7 rounded-md" />
          <span className="text-base font-extrabold tracking-tight text-indigo-700 dark:text-indigo-400">
            {siteName}
          </span>
        </button>

        <div className="flex items-center gap-2">
          {searchOpen ? (
            <div className="flex items-center gap-1">
              <label htmlFor="game-search" className="sr-only">Search games</label>
              <input
                ref={searchRef}
                id="game-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="w-36 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
              <button
                type="button"
                onClick={() => { setSearch(''); setSearchOpen(false); }}
                className="rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:text-gray-200"
                aria-label="Close search"
              >
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setJoinOpen((o) => !o)}
                aria-expanded={joinOpen}
                aria-controls="join-room-panel"
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Join room
              </button>
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Search games"
              >
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </>
          )}
        </div>
      </header>

      {/* ── Join room form (dropdown) ────────────────────────────────────── */}
      {joinOpen && (
        <div id="join-room-panel" className="sticky top-[57px] z-20 border-b border-gray-100 bg-white px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <form onSubmit={handleJoin} className="mx-auto flex max-w-sm flex-col gap-1.5">
            <label htmlFor="home-room-code" className="sr-only">Room code</label>
            <div className="flex gap-2">
              <input
                id="home-room-code"
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value); setCodeError(''); }}
                placeholder="Room code (e.g. K7BNQ2)"
                maxLength={6}
                autoFocus
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={codeError ? true : undefined}
                aria-describedby={codeError ? 'home-room-code-error' : undefined}
                className={[
                  'flex-1 rounded-xl border px-4 py-2 text-sm uppercase tracking-widest',
                  'bg-white dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  codeError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
                ].join(' ')}
              />
              <button
                type="submit"
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-transform duration-150 ease-out hover:bg-indigo-700 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                Join
              </button>
              <button
                type="button"
                onClick={() => { setJoinOpen(false); setCode(''); setCodeError(''); }}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                aria-label="Close join room form"
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
            {codeError && (
              <p id="home-room-code-error" className="text-xs text-red-600 dark:text-red-400" role="alert">{codeError}</p>
            )}
          </form>
        </div>
      )}

      {/* ── Maintenance banner ───────────────────────────────────────────── */}
      {maintenanceMode && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-center dark:border-amber-800 dark:bg-amber-900/20">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
            🚧 The platform is under maintenance. Check back soon!
          </p>
        </div>
      )}

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      {!maintenanceMode && loadState === 'ready' && games.length > 0 && (
        <div className="no-scrollbar flex items-center gap-2 overflow-x-auto border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
          {/* Chips toggle off when re-pressed; previously they could only be
              cleared through the separate Clear button. */}
          <button
            type="button"
            aria-pressed={filter === 'solo'}
            onClick={() => { setFilter((f) => (f === 'solo' ? 'all' : 'solo')); setCategoryFilter(''); }}
            className={chipClass(filter === 'solo')}
          >
            Solo
          </button>
          <button
            type="button"
            aria-pressed={filter === 'favorites'}
            onClick={() => { setFilter((f) => (f === 'favorites' ? 'all' : 'favorites')); setCategoryFilter(''); }}
            className={chipClass(filter === 'favorites')}
          >
            <span aria-hidden="true">❤️</span> Favorites
          </button>
          <label htmlFor="category-filter" className="sr-only">Filter by category</label>
          <select
            id="category-filter"
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setFilter('all'); }}
            className={[
              'shrink-0 cursor-pointer appearance-none rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
              'focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-950',
              categoryFilter
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800',
            ].join(' ')}
          >
            <option value="">Categories ▾</option>
            {allTags.map((tag) => (
              <option key={tag} value={tag} className="capitalize bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                {tag.charAt(0).toUpperCase() + tag.slice(1)}
              </option>
            ))}
          </select>
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="shrink-0 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <span aria-hidden="true">✕</span> Clear
            </button>
          )}
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      {heroGame && !maintenanceMode && loadState === 'ready' && (
        <HeroSection
          game={heroGame}
          onPlay={() => void navigate(gameRoute(heroGame.id))}
          isFavorite={favorites.includes(heroGame.id)}
          onToggleFavorite={() => toggleFavorite(heroGame.id)}
        />
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <main className="pb-24" aria-busy={loadState === 'loading'}>
        {loadState === 'loading' && !maintenanceMode && (
          <div className="py-3">
            <p className="sr-only" role="status">Loading games…</p>
            <div className="mb-3 mx-4 h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="no-scrollbar flex gap-3 overflow-x-hidden px-4 pb-2">
              {[0, 1, 2, 3].map((i) => <GameCardSkeleton key={i} />)}
            </div>
          </div>
        )}

        {loadState === 'error' && !maintenanceMode && (
          <div className="px-4 py-16 text-center" role="alert">
            <p className="text-2xl" aria-hidden="true">📡</p>
            <p className="mt-2 font-semibold text-gray-900 dark:text-gray-100">Couldn't load games</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Check your connection and try again.
            </p>
            <button
              type="button"
              onClick={() => fetchGames()}
              className="mt-4 rounded-full bg-indigo-600 px-5 py-2 text-sm font-semibold text-white transition-transform duration-150 ease-out hover:bg-indigo-700 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              Retry
            </button>
          </div>
        )}

        {loadState === 'ready' && !maintenanceMode && (
          tagGroups.length === 0 ? (
            <div className="px-4 py-16 text-center">
              <p className="text-2xl" aria-hidden="true">
                {filter === 'favorites' && favorites.length === 0 ? '🤍' : '🔍'}
              </p>
              <p className="mt-2 font-semibold text-gray-900 dark:text-gray-100">
                {games.length === 0
                  ? 'No games available yet'
                  : filter === 'favorites' && favorites.length === 0
                    ? 'No favorites yet'
                    : 'No games match your search'}
              </p>
              <p className="mt-1 text-sm text-pretty text-gray-500 dark:text-gray-400">
                {games.length === 0
                  ? 'Games will appear here as soon as they are enabled.'
                  : filter === 'favorites' && favorites.length === 0
                    ? 'Tap the heart on any game to pin it here.'
                    : 'Try a different word, or clear your filters.'}
              </p>
              {/* Every empty state gets one clear next action. */}
              {games.length > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-4 rounded-full bg-indigo-600 px-5 py-2 text-sm font-semibold text-white transition-transform duration-150 ease-out hover:bg-indigo-700 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  Browse all games
                </button>
              )}
            </div>
          ) : (
            tagGroups.map(({ tag, games: tagGames }) => (
              <CategoryRow
                key={tag}
                tag={tag}
                games={tagGames}
                onPlay={(g) => void navigate(gameRoute(g.id))}
              />
            ))
          )
        )}
      </main>
    </div>
  );
}
